// src/env/base.ts
var baseConfig = {
  api: {
    baseUrl: "http://localhost:8080",
    timeout: 3e4,
    retryAttempts: 3,
    retryDelay: 1e3
  },
  features: {
    offlineMode: false,
    analytics: false,
    crashReporting: false,
    pushNotifications: false,
    debugMode: true
  },
  analytics: {
    enabled: false,
    trackingId: void 0
  },
  cache: {
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    gcTime: 24 * 60 * 60 * 1e3,
    // 24 hours
    retryAttempts: 3
  },
  storage: {
    prefix: "pc_",
    encrypted: false
  }
};

// src/env/development.ts
var developmentConfig = {
  api: {
    baseUrl: "http://localhost:8080",
    timeout: 6e4,
    // Longer timeout for debugging
    retryAttempts: 1
  },
  keycloak: {
    url: "http://localhost:8180",
    realm: "parental-control"
  },
  features: {
    offlineMode: true,
    analytics: false,
    crashReporting: false,
    pushNotifications: false,
    debugMode: true
  },
  sentry: {
    dsn: "",
    environment: "development",
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0
  },
  analytics: {
    enabled: false
  },
  cache: {
    staleTime: 1 * 60 * 1e3,
    // 1 minute (shorter for dev)
    gcTime: 5 * 60 * 1e3
    // 5 minutes
  },
  storage: {
    prefix: "pc_dev_",
    encrypted: false
  }
};

// src/env/staging.ts
var stagingConfig = {
  api: {
    baseUrl: "https://api.staging.parentalcontrol.com",
    timeout: 3e4,
    retryAttempts: 3
  },
  keycloak: {
    url: "https://auth.staging.parentalcontrol.com",
    realm: "parental-control"
  },
  features: {
    offlineMode: true,
    analytics: true,
    crashReporting: true,
    pushNotifications: true,
    debugMode: true
  },
  sentry: {
    dsn: "https://staging@sentry.io/parental-control",
    environment: "staging",
    tracesSampleRate: 0.5,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1
  },
  analytics: {
    enabled: true,
    trackingId: "UA-STAGING-001"
  },
  cache: {
    staleTime: 3 * 60 * 1e3,
    // 3 minutes
    gcTime: 12 * 60 * 60 * 1e3
    // 12 hours
  },
  storage: {
    prefix: "pc_stg_",
    encrypted: true
  }
};

// src/env/production.ts
var productionConfig = {
  api: {
    baseUrl: "https://api.parentalcontrol.com",
    timeout: 3e4,
    retryAttempts: 3,
    retryDelay: 1e3
  },
  keycloak: {
    url: "https://auth.parentalcontrol.com",
    realm: "parental-control"
  },
  features: {
    offlineMode: true,
    analytics: true,
    crashReporting: true,
    pushNotifications: true,
    debugMode: false
  },
  sentry: {
    dsn: "https://production@sentry.io/parental-control",
    environment: "production",
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1
  },
  analytics: {
    enabled: true,
    trackingId: "UA-PROD-001"
  },
  cache: {
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    gcTime: 24 * 60 * 60 * 1e3
    // 24 hours
  },
  storage: {
    prefix: "pc_",
    encrypted: true
  }
};

// src/env/index.ts
function getEnvConfig(env) {
  const configs = {
    development: developmentConfig,
    staging: stagingConfig,
    production: productionConfig
  };
  return configs[env];
}
function detectEnvironment() {
  if (typeof import.meta !== "undefined" && import.meta.env) {
    const mode = import.meta.env.MODE;
    if (mode === "production") return "production";
    if (mode === "staging") return "staging";
    return "development";
  }
  if (typeof process !== "undefined" && process.env) {
    const nodeEnv = process.env.NODE_ENV;
    if (nodeEnv === "production") return "production";
    if (nodeEnv === "staging") return "staging";
    return "development";
  }
  return "development";
}

// src/create-config.ts
function deepMerge(target, ...sources) {
  const result = { ...target };
  for (const source of sources) {
    if (!source) continue;
    for (const key of Object.keys(source)) {
      const sourceValue = source[key];
      const targetValue = result[key];
      if (sourceValue !== void 0 && typeof sourceValue === "object" && sourceValue !== null && !Array.isArray(sourceValue) && typeof targetValue === "object" && targetValue !== null && !Array.isArray(targetValue)) {
        result[key] = deepMerge(
          targetValue,
          sourceValue
        );
      } else if (sourceValue !== void 0) {
        result[key] = sourceValue;
      }
    }
  }
  return result;
}
function createConfig(environment, clientConfig, runtimeOverrides) {
  const envConfig = getEnvConfig(environment);
  const config = deepMerge(
    {
      environment,
      app: {
        name: "Parental Control",
        version: "1.0.0",
        buildNumber: "1"
      },
      keycloak: {
        url: "",
        realm: "parental-control",
        clientId: "parent-web"
      },
      sentry: {
        dsn: "",
        environment,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1
      },
      ...baseConfig
    },
    envConfig,
    clientConfig,
    runtimeOverrides
  );
  return config;
}
function validateConfig(config) {
  const errors = [];
  if (!config.api.baseUrl) {
    errors.push("api.baseUrl is required");
  }
  if (!config.keycloak.url) {
    errors.push("keycloak.url is required");
  }
  if (!config.keycloak.realm) {
    errors.push("keycloak.realm is required");
  }
  if (!config.keycloak.clientId) {
    errors.push("keycloak.clientId is required");
  }
  if (config.environment === "production" && config.features.crashReporting && !config.sentry.dsn) {
    errors.push("sentry.dsn is required when crashReporting is enabled in production");
  }
  return {
    valid: errors.length === 0,
    errors
  };
}
function getConfigValue(config, path) {
  const keys = path.split(".");
  let result = config;
  for (const key of keys) {
    if (result === null || result === void 0 || typeof result !== "object") {
      return void 0;
    }
    result = result[key];
  }
  return result;
}

// src/clients/parent-web.ts
var parentWebBase = {
  app: {
    name: "Parental Control",
    version: "1.0.0",
    buildNumber: "1"
  },
  keycloak: {
    url: "",
    realm: "parental-control",
    clientId: "parent-web"
  },
  storage: {
    prefix: "pc_parent_",
    encrypted: false
    // Web uses httpOnly cookies for sensitive data
  }
};
function getParentWebConfig(env) {
  return createConfig(env, parentWebBase);
}
function createParentWebConfigFromEnv(env) {
  return {
    api: {
      baseUrl: env.VITE_API_URL,
      timeout: 3e4,
      retryAttempts: 3,
      retryDelay: 1e3
    },
    keycloak: {
      url: env.VITE_KEYCLOAK_URL,
      realm: env.VITE_KEYCLOAK_REALM || "parental-control",
      clientId: env.VITE_KEYCLOAK_CLIENT_ID || "parent-web"
    },
    sentry: {
      dsn: env.VITE_SENTRY_DSN || "",
      environment: "production",
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1
    },
    analytics: {
      enabled: Boolean(env.VITE_ANALYTICS_ID),
      trackingId: env.VITE_ANALYTICS_ID
    }
  };
}

// src/clients/parent-mobile.ts
var parentMobileBase = {
  app: {
    name: "Parental Control",
    version: "1.0.0",
    buildNumber: "1"
  },
  keycloak: {
    url: "",
    realm: "parental-control",
    clientId: "parent-mobile"
  },
  features: {
    offlineMode: true,
    analytics: true,
    crashReporting: true,
    pushNotifications: true,
    debugMode: false
  },
  storage: {
    prefix: "pc_parent_",
    encrypted: true
    // Mobile uses secure storage
  }
};
function getParentMobileConfig(env) {
  return createConfig(env, parentMobileBase);
}
function createParentMobileConfigFromExpo(constants) {
  return {
    api: {
      baseUrl: constants.API_URL,
      timeout: 3e4,
      retryAttempts: 3,
      retryDelay: 1e3
    },
    keycloak: {
      url: constants.KEYCLOAK_URL,
      realm: constants.KEYCLOAK_REALM || "parental-control",
      clientId: constants.KEYCLOAK_CLIENT_ID || "parent-mobile"
    },
    sentry: {
      dsn: constants.SENTRY_DSN || "",
      environment: "production",
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1
    }
  };
}

// src/clients/child-mobile.ts
var childMobileBase = {
  app: {
    name: "Parental Control Kids",
    version: "1.0.0",
    buildNumber: "1"
  },
  keycloak: {
    url: "",
    realm: "parental-control",
    clientId: "child-mobile"
  },
  features: {
    offlineMode: true,
    // Critical for child app
    analytics: true,
    crashReporting: true,
    pushNotifications: false,
    // No push for child
    debugMode: false
  },
  storage: {
    prefix: "pc_child_",
    encrypted: true
  },
  cache: {
    staleTime: 10 * 60 * 1e3,
    // 10 minutes - longer for child
    gcTime: 48 * 60 * 60 * 1e3,
    // 48 hours - supports offline
    retryAttempts: 5
    // More retries for child
  }
};
function getChildMobileConfig(env) {
  return createConfig(env, childMobileBase);
}
function createChildMobileConfigFromExpo(constants) {
  return {
    api: {
      baseUrl: constants.API_URL,
      timeout: 3e4,
      retryAttempts: 5,
      // More retries for offline resilience
      retryDelay: 2e3
    },
    keycloak: {
      url: constants.KEYCLOAK_URL,
      realm: constants.KEYCLOAK_REALM || "parental-control",
      clientId: constants.KEYCLOAK_CLIENT_ID || "child-mobile"
    },
    sentry: {
      dsn: constants.SENTRY_DSN || "",
      environment: "production",
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1
    }
  };
}
var childAppSettings = {
  /** Minimum exercises before showing stats */
  minExercisesForStats: 5,
  /** Points per correct answer multiplier by difficulty */
  pointsMultiplier: {
    EASY: 1,
    MEDIUM: 1.5,
    HARD: 2
  },
  /** Streak bonus thresholds */
  streakBonuses: {
    3: 1.1,
    // 10% bonus at 3 days
    7: 1.25,
    // 25% bonus at 7 days
    14: 1.5,
    // 50% bonus at 14 days
    30: 2
    // 100% bonus at 30 days
  },
  /** Animation durations in ms */
  animations: {
    pointsPopup: 1500,
    levelUp: 3e3,
    confetti: 4e3
  },
  /** Haptic feedback patterns */
  haptics: {
    correctAnswer: "success",
    wrongAnswer: "error",
    levelUp: "heavy",
    buttonPress: "light"
  }
};

// src/clients/admin-console.ts
var adminConsoleBase = {
  app: {
    name: "Parental Control Admin",
    version: "1.0.0",
    buildNumber: "1"
  },
  keycloak: {
    url: "",
    realm: "parental-control",
    clientId: "admin-console"
  },
  features: {
    offlineMode: false,
    // Admin needs real-time data
    analytics: true,
    crashReporting: true,
    pushNotifications: false,
    debugMode: false
  },
  storage: {
    prefix: "pc_admin_",
    encrypted: false
  },
  cache: {
    staleTime: 1 * 60 * 1e3,
    // 1 minute - admin needs fresh data
    gcTime: 30 * 60 * 1e3,
    // 30 minutes
    retryAttempts: 2
  }
};
function getAdminConsoleConfig(env) {
  return createConfig(env, adminConsoleBase);
}
function createAdminConsoleConfigFromEnv(env) {
  return {
    api: {
      baseUrl: env.VITE_API_URL,
      timeout: 6e4,
      // Longer timeout for admin operations
      retryAttempts: 2,
      retryDelay: 1e3
    },
    keycloak: {
      url: env.VITE_KEYCLOAK_URL,
      realm: env.VITE_KEYCLOAK_REALM || "parental-control",
      clientId: env.VITE_KEYCLOAK_CLIENT_ID || "admin-console"
    },
    sentry: {
      dsn: env.VITE_SENTRY_DSN || "",
      environment: "production",
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.5,
      // Higher for admin debugging
      replaysOnErrorSampleRate: 1
    },
    analytics: {
      enabled: Boolean(env.VITE_ANALYTICS_ID),
      trackingId: env.VITE_ANALYTICS_ID
    }
  };
}
var adminSettings = {
  /** Default page size for tables */
  defaultPageSize: 20,
  /** Available page sizes */
  pageSizeOptions: [10, 20, 50, 100],
  /** Auto-refresh interval for dashboard (ms) */
  dashboardRefreshInterval: 6e4,
  /** Max export rows */
  maxExportRows: 1e4,
  /** Session timeout warning (ms before expiry) */
  sessionTimeoutWarning: 5 * 60 * 1e3
};

// src/clients/index.ts
function getClientConfig(clientId, env) {
  const configGetters = {
    "parent-web": getParentWebConfig,
    "parent-mobile": getParentMobileConfig,
    "child-mobile": getChildMobileConfig,
    "admin-console": getAdminConsoleConfig
  };
  return configGetters[clientId](env);
}

// src/features/index.ts
var featureFlags = {
  new_dashboard: {
    enabled: false,
    percentage: 0,
    description: "New dashboard design with improved UX"
  },
  offline_mode: {
    enabled: true,
    environments: ["production", "staging"],
    description: "Enable offline support for mobile apps"
  },
  gamification_v2: {
    enabled: false,
    percentage: 10,
    roles: ["ROLE_CHILD"],
    description: "New gamification features (achievements, badges)"
  },
  push_notifications: {
    enabled: true,
    roles: ["ROLE_PARENT"],
    environments: ["production"],
    description: "Push notifications for parents"
  },
  dark_mode: {
    enabled: true,
    description: "Dark mode support"
  },
  biometric_auth: {
    enabled: true,
    environments: ["production", "staging"],
    description: "Biometric authentication (Face ID, fingerprint)"
  },
  export_data: {
    enabled: true,
    roles: ["ROLE_PARENT", "ROLE_ADMIN"],
    description: "Export data to CSV/PDF"
  },
  bulk_actions: {
    enabled: true,
    roles: ["ROLE_ADMIN"],
    description: "Bulk actions in admin console"
  }
};
function isFeatureEnabled(flag, context) {
  const config = featureFlags[flag];
  if (!config.enabled) {
    return false;
  }
  if (config.environments && !config.environments.includes(context.environment)) {
    return false;
  }
  if (config.roles && context.role && !config.roles.includes(context.role)) {
    return false;
  }
  if (config.percentage !== void 0 && context.userId) {
    const hash = hashString(context.userId + flag);
    if (hash > config.percentage) {
      return false;
    }
  }
  return true;
}
function getEnabledFeatures(context) {
  return Object.keys(featureFlags).filter(
    (flag) => isFeatureEnabled(flag, context)
  );
}
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash % 100);
}

// src/constants.ts
var API_ENDPOINTS = {
  // Auth
  AUTH: "/api/auth",
  // Users
  USERS: "/api/users",
  USERS_ME: "/api/users/me",
  // Children
  CHILDREN: "/api/children",
  // Apps
  APPS: "/api/apps",
  APPS_ACCESS: "/api/apps/access",
  // Exercises
  EXERCISES: "/api/exercises",
  EXERCISES_SUBMIT: "/api/exercises/:id/submit",
  EXERCISES_RESULTS: "/api/exercises/results",
  // Stats
  STATS: "/api/stats",
  STATS_ME: "/api/stats/me",
  STATS_CHILD: "/api/stats/children/:childId",
  STATS_DAILY: "/api/stats/children/:childId/daily",
  STATS_WEEKLY: "/api/stats/children/:childId/daily/recent",
  // Admin
  ADMIN: "/api/admin",
  ADMIN_STATS: "/api/admin/stats",
  ADMIN_USERS: "/api/admin/users",
  ADMIN_EXERCISES: "/api/admin/exercises",
  ADMIN_ACTIONS: "/api/admin/actions"
};
var STORAGE_KEYS = {
  // Auth
  ACCESS_TOKEN: "access_token",
  REFRESH_TOKEN: "refresh_token",
  ID_TOKEN: "id_token",
  USER: "user",
  // App state
  THEME: "theme",
  LANGUAGE: "language",
  ONBOARDING_COMPLETE: "onboarding_complete",
  // Cache
  QUERY_CACHE: "query_cache",
  OFFLINE_QUEUE: "offline_queue",
  // User preferences
  NOTIFICATIONS_ENABLED: "notifications_enabled",
  BIOMETRIC_ENABLED: "biometric_enabled"
};
var QUERY_KEYS = {
  // Users
  USER: ["user"],
  USER_ME: ["user", "me"],
  // Children
  CHILDREN: ["children"],
  CHILDREN_LIST: ["children", "list"],
  CHILD: (id) => ["children", id],
  CHILD_STATS: (id) => ["children", id, "stats"],
  CHILD_APPS: (id) => ["children", id, "apps"],
  // Apps
  APPS: ["apps"],
  APP: (id) => ["apps", id],
  APP_ACCESS: (packageName) => ["apps", "access", packageName],
  // Exercises
  EXERCISES: ["exercises"],
  EXERCISE: (id) => ["exercises", id],
  EXERCISE_RESULTS: ["exercises", "results"],
  // Stats
  STATS: ["stats"],
  STATS_ME: ["stats", "me"],
  STATS_CHILD: (childId) => ["stats", "child", childId],
  STATS_DAILY: (childId, date) => ["stats", "daily", childId, date],
  STATS_WEEKLY: (childId) => ["stats", "weekly", childId],
  // Admin
  ADMIN_STATS: ["admin", "stats"],
  ADMIN_USERS: ["admin", "users"],
  ADMIN_EXERCISES: ["admin", "exercises"],
  ADMIN_ACTIONS: ["admin", "actions"]
};
var TIME = {
  SECOND: 1e3,
  MINUTE: 60 * 1e3,
  HOUR: 60 * 60 * 1e3,
  DAY: 24 * 60 * 60 * 1e3,
  WEEK: 7 * 24 * 60 * 60 * 1e3
};
var VALIDATION = {
  // Child profile
  CHILD_NAME_MIN: 1,
  CHILD_NAME_MAX: 50,
  CHILD_AGE_MIN: 3,
  CHILD_AGE_MAX: 18,
  // Exercise
  EXERCISE_LEVEL_MIN: 1,
  EXERCISE_LEVEL_MAX: 10,
  EXERCISE_OPTIONS_MIN: 2,
  EXERCISE_OPTIONS_MAX: 6,
  // App
  DAILY_LIMIT_MIN: 0,
  DAILY_LIMIT_MAX: 1440,
  // 24 hours in minutes
  // Pagination
  PAGE_SIZE_DEFAULT: 20,
  PAGE_SIZE_MIN: 1,
  PAGE_SIZE_MAX: 100
};
var ERROR_CODES = {
  // Auth
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  INVALID_TOKEN: "INVALID_TOKEN",
  // Validation
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INVALID_INPUT: "INVALID_INPUT",
  // Resources
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  ALREADY_EXISTS: "ALREADY_EXISTS",
  // Server
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  // Network
  NETWORK_ERROR: "NETWORK_ERROR",
  TIMEOUT: "TIMEOUT"
};

export { API_ENDPOINTS, ERROR_CODES, QUERY_KEYS, STORAGE_KEYS, TIME, VALIDATION, adminSettings, baseConfig, childAppSettings, createAdminConsoleConfigFromEnv, createChildMobileConfigFromExpo, createConfig, createParentMobileConfigFromExpo, createParentWebConfigFromEnv, detectEnvironment, developmentConfig, featureFlags, getAdminConsoleConfig, getChildMobileConfig, getClientConfig, getConfigValue, getEnabledFeatures, getEnvConfig, getParentMobileConfig, getParentWebConfig, isFeatureEnabled, productionConfig, stagingConfig, validateConfig };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map